import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { formatArea } from "@/lib/format";

function Field({ label, children, full }) {
  return <div className={`space-y-1.5 ${full ? "sm:col-span-2" : ""}`}><Label className="text-xs font-semibold text-slate-600">{label}</Label>{children}</div>;
}

export function ProgressDialog({ open, onOpenChange, phase, onSaved }) {
  const [form, setForm] = useState({ area_completed: "", date: "", zone: "", flooring_system: "", description: "", materials_used: "", labour: "", remarks: "" });
  const [saving, setSaving] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState("");

  useEffect(() => {
    if (open) { setConfirmMsg(""); setForm({ area_completed: "", date: new Date().toISOString().slice(0, 10), zone: "", flooring_system: "", description: "", materials_used: "", labour: "", remarks: "" }); }
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (confirm = false) => {
    if (!form.area_completed || Number(form.area_completed) <= 0) { toast.error("Enter the area completed"); return; }
    setSaving(true);
    try {
      await api.post(`/phases/${phase.id}/progress`, { ...form, area_completed: Number(form.area_completed), confirm });
      toast.success("Progress recorded");
      onOpenChange(false); onSaved && onSaved();
    } catch (e) {
      if (e.response?.status === 409) setConfirmMsg(apiError(e.response.data.detail));
      else toast.error(apiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (!phase) return null;
  const remaining = (phase.planned_area || 0) - (phase.completed_area || 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg gap-0 p-0 max-h-[92vh] overflow-hidden flex flex-col">
        <DialogHeader className="border-b border-slate-100 px-6 py-4">
          <DialogTitle className="text-lg">Update Progress — {phase.name}</DialogTitle>
          <p className="text-xs text-slate-500">Completed {formatArea(phase.completed_area || 0)} of {formatArea(phase.planned_area || 0)} · {formatArea(Math.max(remaining, 0))} remaining</p>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-4 overflow-y-auto px-6 py-5 sm:grid-cols-2">
          <Field label="Area Completed (Sq.Ft)"><Input data-testid="progress-area" type="number" value={form.area_completed} onChange={(e) => set("area_completed", e.target.value)} placeholder="e.g. 750" /></Field>
          <Field label="Date"><Input data-testid="progress-date" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} /></Field>
          <Field label="Location / Zone"><Input value={form.zone} onChange={(e) => set("zone", e.target.value)} placeholder="e.g. North Bay" /></Field>
          <Field label="Flooring System"><Input value={form.flooring_system} onChange={(e) => set("flooring_system", e.target.value)} /></Field>
          <Field label="Work Description" full><Textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} /></Field>
          <Field label="Materials Used"><Input value={form.materials_used} onChange={(e) => set("materials_used", e.target.value)} placeholder="Foundation only" /></Field>
          <Field label="Labour"><Input value={form.labour} onChange={(e) => set("labour", e.target.value)} placeholder="e.g. 6 workers" /></Field>
          <Field label="Remarks" full><Textarea rows={2} value={form.remarks} onChange={(e) => set("remarks", e.target.value)} /></Field>
          {confirmMsg && (
            <div className="sm:col-span-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /><span>{confirmMsg}</span>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-100 px-6 py-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          {confirmMsg ? (
            <Button data-testid="progress-confirm" onClick={() => submit(true)} disabled={saving} className="bg-amber-600 font-semibold hover:bg-amber-700">Confirm & Record</Button>
          ) : (
            <Button data-testid="progress-submit" onClick={() => submit(false)} disabled={saving} className="font-semibold">{saving ? "Saving…" : "Record Progress"}</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
